/**
 * Map API functions.
 *
 * All functions respect tenant isolation and authentication.
 * Search and geocoding go through the backend to keep the Mapbox secret token safe.
 */

import { apiGet } from '../../../utils/api';
import type {
  RawFarmPlot,
  RawTree,
  RawFarmer,
  RawCohort,
  RawWarehouse,
  MapSearchResult,
  MapField,
  MapTree,
  MapFarmer,
  MapCohort,
  MapWarehouse,
} from '../types/mapTypes';
import { toGeoCoord, toBoundary, parseBoundaryMaybeString, ensureCenter } from '../utils/coordinates';
import type { GeoCoord } from '../types/mapTypes';

// ─── Bounding-box layer endpoints ───────────────────────────────────

export interface BboxQuery {
  bbox?: string; // "west,south,east,north"
  zoom?: number;
}

/**
 * Fetch farm plots as raw data.
 */
export async function fetchFarmPlots(params?: {
  cropType?: string;
  farmerId?: number;
  status?: string;
}, signal?: AbortSignal): Promise<RawFarmPlot[]> {
  const query: Record<string, string> = {};
  if (params?.cropType) query.crop_type = params.cropType;
  if (params?.farmerId) query.farmer_id = String(params.farmerId);
  if (params?.status) query.status = params.status;
  return apiGet<RawFarmPlot[]>('/api/maps/farm-plots', query, { signal });
}

/**
 * Fetch trees as raw data.
 */
export async function fetchTrees(params?: {
  cropType?: string;
  cohortId?: number;
  farmerId?: number;
  healthStatus?: string;
  limit?: number;
}, signal?: AbortSignal): Promise<RawTree[]> {
  const query: Record<string, string> = { limit: String(params?.limit || 2000) };
  if (params?.cropType) query.crop_type = params.cropType;
  if (params?.cohortId) query.cohort_id = String(params.cohortId);
  if (params?.farmerId) query.farmer_id = String(params.farmerId);
  if (params?.healthStatus) query.health_status = params.healthStatus;
  return apiGet<RawTree[]>('/api/maps/trees', query, { signal });
}

/**
 * Fetch farmers with geo data.
 */
export async function fetchFarmersGeo(params?: {
  cohortId?: number;
}, signal?: AbortSignal): Promise<RawFarmer[]> {
  const query: Record<string, string> = {};
  if (params?.cohortId) query.cohort_id = String(params.cohortId);
  return apiGet<RawFarmer[]>('/api/maps/farmers', query, { signal });
}

/**
 * Fetch cohorts with boundaries.
 */
export async function fetchCohortsGeo(params?: {
  cohortId?: number;
}, signal?: AbortSignal): Promise<RawCohort[]> {
  const query: Record<string, string> = {};
  if (params?.cohortId) query.cohort_id = String(params.cohortId);
  return apiGet<RawCohort[]>('/api/maps/cohorts', query, { signal });
}

/**
 * Fetch warehouses.
 */
export async function fetchWarehousesGeo(signal?: AbortSignal): Promise<RawWarehouse[]> {
  return apiGet<RawWarehouse[]>('/api/maps/warehouses', undefined, { signal });
}

/**
 * Fetch map stats.
 */
export async function fetchMapStats(): Promise<{
  total_cohorts: number;
  total_farmers: number;
  total_farm_plots?: number;
  total_warehouses: number;
  total_aggregation_centers: number;
  total_area_hectares: number;
}> {
  return apiGet('/api/maps/stats');
}

// ─── Search endpoints (backend-proxied) ────────────────────────────

export interface SearchParams {
  q: string;
  types?: string; // comma-separated: "farmer,field,tree,place"
  proximity?: string; // "lng,lat"
  limit?: number;
}

/**
 * Unified search: places + internal tenant entities.
 */
export async function searchMap(params: SearchParams): Promise<MapSearchResult[]> {
  const query: Record<string, string> = { q: params.q };
  if (params.types) query.types = params.types;
  if (params.proximity) query.proximity = params.proximity;
  if (params.limit) query.limit = String(params.limit);
  return apiGet<MapSearchResult[]>('/api/maps/search', query);
}

export interface ReverseGeocodeParams {
  lat: number;
  lng: number;
}

export interface ReverseGeocodeResult {
  display_name: string;
  address?: Record<string, string>;
}

/**
 * Reverse geocode a coordinate (backend-proxied Mapbox API).
 */
export async function reverseGeocode(
  params: ReverseGeocodeParams,
): Promise<ReverseGeocodeResult> {
  return apiGet<ReverseGeocodeResult>('/api/maps/reverse-geocode', {
    lat: String(params.lat),
    lng: String(params.lng),
  });
}

export interface SuggestParams {
  q: string;
  sessionToken?: string;
  proximity?: string;
  limit?: number;
}

export interface SuggestResult {
  id: string;
  label: string;
  secondaryLabel?: string;
  center?: GeoCoord;
  bbox?: GeoCoord[];
  source: 'external' | 'internal';
}

/**
 * Autocomplete suggestions (backend-proxied Mapbox Search).
 */
export async function suggestPlaces(params: SuggestParams): Promise<SuggestResult[]> {
  const query: Record<string, string> = { q: params.q };
  if (params.sessionToken) query.session_token = params.sessionToken;
  if (params.proximity) query.proximity = params.proximity;
  if (params.limit) query.limit = String(params.limit);
  return apiGet<SuggestResult[]>('/api/maps/suggest', query);
}

export async function retrievePlace(mapboxId: string, sessionToken: string): Promise<MapSearchResult> {
  return apiGet<MapSearchResult>(`/api/maps/retrieve/${encodeURIComponent(mapboxId)}`, {
    session_token: sessionToken,
  });
}

// ─── Field CRUD ─────────────────────────────────────────────────────

export interface CreateFieldPayload {
  name: string;
  crop_type: string;
  area_hectares: number;
  expected_tree_count?: number;
  assigned_agronomist_id?: number | null;
  boundary_coordinates: GeoCoord[];
  center_lat: number;
  center_lng: number;
  farmer_ids?: number[];
}

/**
 * Create a farm plot with boundary.
 */
export async function createField(payload: CreateFieldPayload): Promise<{ id: number }> {
  const response = await fetch('/api/maps/farm-plots', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('aaywa_token') || ''}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Create failed' }));
    throw new Error(err.message || 'Failed to create field');
  }
  return response.json();
}

export interface UpdateFieldPayload {
  name?: string;
  crop_type?: string;
  area_hectares?: number;
  boundary_coordinates?: GeoCoord[];
  center_lat?: number;
  center_lng?: number;
}

/**
 * Update a farm plot.
 */
export async function updateField(
  fieldId: number,
  payload: UpdateFieldPayload,
): Promise<void> {
  const response = await fetch(`/api/maps/farm-plots/${fieldId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('aaywa_token') || ''}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Update failed' }));
    throw new Error(err.message || 'Failed to update field');
  }
}

// ─── Data normalization ─────────────────────────────────────────────

/**
 * Normalize raw API responses into domain types.
 * This is the single conversion point from DB shapes to Map* types.
 */

export function normalizeFarmPlots(raw: RawFarmPlot[]): MapField[] {
  return raw.map((plot) => {
    const boundary = parseBoundaryMaybeString(plot.boundary_coordinates);
    const center = ensureCenter(
      plot.center_lat != null && plot.center_lng != null
        ? [plot.center_lng, plot.center_lat]
        : null,
      boundary,
    );

    return {
      id: plot.id,
      plotCode: plot.plot_code,
      name: plot.name || plot.plot_code || 'Mapped field',
      cropType: plot.crop_type,
      status: plot.status,
      areaHectares: plot.area_hectares,
      center: center ?? undefined,
      boundary,
      farmerId: plot.farmer_id,
      farmerName: plot.farmer_name,
      primaryFarmerName: plot.primary_farmer_name || plot.farmer_name,
      farmers: (plot.farmers || []).map((f) => ({
        id: f.id,
        name: f.name,
        roleOnField: f.role_on_field,
        isPrimary: f.is_primary,
      })),
      farmerCount: plot.farmer_count ?? (plot.farmers?.length ?? 0),
      expectedTreeCount: plot.expected_tree_count ?? 0,
      gridRows: plot.grid_rows,
      gridColumns: plot.grid_columns,
      assignedAgronomistId: plot.assigned_agronomist_id,
      assignedAgronomistName: plot.assigned_agronomist_name,
      agronomists: (plot.agronomists || []).map((person) => ({
        id: person.id,
        name: person.name,
        email: person.email,
        isPrimary: person.is_primary,
      })),
      agronomistCount: plot.agronomist_count ?? (plot.agronomists?.length ?? 0),
      assignedFacilitatorId: plot.assigned_field_facilitator_id,
      assignedFacilitatorName: plot.assigned_field_facilitator_name,
      treeCount: plot.tree_count ?? 0,
      mappedTreeCount: plot.mapped_tree_count ?? 0,
      activityCount: plot.activity_count ?? 0,
      diaryCount: plot.diary_count ?? 0,
      latestActivityStatus: plot.latest_activity_status,
      latestDiaryStatus: plot.latest_diary_status,
      cohortId: plot.cohort_id,
      cohortName: plot.cohort_name,
    };
  });
}

export function normalizeTrees(raw: RawTree[]): MapTree[] {
  const result: MapTree[] = [];
  for (const t of raw) {
    const pos = toGeoCoord([t.longitude, t.latitude], 'lng-lat');
    if (!pos) continue;
    result.push({
      id: t.id,
      treeCode: t.tree_code,
      cropType: t.crop_type,
      variety: t.variety,
      position: pos,
      healthStatus: t.health_status,
      treeStatus: t.tree_status,
      productionStatus: t.production_status,
      farmerId: t.farmer_id,
      farmerName: t.farmer_name,
      agronomistId: t.assigned_agronomist_id,
      agronomistName: t.assigned_agronomist_name,
      cohortId: t.cohort_id,
      cohortName: t.cohort_name,
      plotCode: t.plot_code,
      plotName: t.plot_name,
      openIssueCount: t.open_issue_count ?? 0,
      latestIssuePhoto: t.latest_issue_photo,
    });
  }
  return result;
}

export function normalizeFarmers(raw: RawFarmer[]): MapFarmer[] {
  return raw
    .map((f) => {
      const boundary = toBoundary(f.plot_boundary_coordinates);
      const location = toGeoCoord(f.location_coordinates);
      return {
        id: f.id,
        name: f.name,
        householdType: f.household_type,
        boundary,
        location: location ?? undefined,
        cohortId: f.cohort_id,
        cohortName: f.cohort_name,
        areaHectares: f.plot_area_hectares,
      };
    })
    .filter((f) => f.boundary.length > 0 || f.location != null);
}

export function normalizeCohorts(raw: RawCohort[]): MapCohort[] {
  return raw
    .map((c) => {
      const boundary = parseBoundaryMaybeString(c.boundary_coordinates);
      return {
        id: c.id,
        name: c.name,
        croppingSystem: c.cropping_system,
        boundary,
        boundaryColor: c.boundary_color,
        farmerCount: Number(c.farmer_count),
        totalAreaHectares: Number(c.total_area_hectares),
      };
    })
    .filter((c) => c.boundary.length >= 3);
}

export function normalizeWarehouses(raw: RawWarehouse[]): MapWarehouse[] {
  return raw
    .map((w) => {
      const pos = toGeoCoord([w.location_lng, w.location_lat], 'lng-lat');
      if (!pos) return null;
      return {
        id: w.id,
        name: w.name,
        type: w.type,
        position: pos,
        locationName: w.location_name,
        capacityKg: w.capacity_kg,
        currentUsageKg: w.current_usage_kg,
        temperatureCelsius: w.temperature_celsius,
        status: w.status,
        usagePercentage: w.usage_percentage,
      };
    })
    .filter((w): w is MapWarehouse => w !== null);
}
